import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { GatsbyImage } from 'gatsby-plugin-image'
import React from 'react'

import { hexToHSL } from '../../../helpers/common'
import { LearnArticleCardProps } from '../../../interfaces/learn'
import { Link } from './../../Link'
import VerticalTextDivider from './../../VerticalTextDivider'

const LearnArticleCard: React.FunctionComponent<LearnArticleCardProps> = ({
  title,
  description,
  categoryColor,
  isSmall = false,
  isSoon,
  author,
  createdAt,
  logoSrc,
  articleUrl,
}) => {
  const { h, s, l } = hexToHSL(categoryColor)
  const date = dayjs(createdAt).format('D MMM YYYY')

  return (
    <React.Fragment>
      {isSmall ? (
        <Link href={`/product-learn/${articleUrl}/`}>
          <div css={containerStyle(h, s, l, isSmall)}>
            {logoSrc && (
              <div className={'background-gradient'} css={iconStyle(h, s, l)}>
                <GatsbyImage image={logoSrc.gatsbyImageData} alt={logoSrc.title} />
              </div>
            )}
            <div>
              <Link className={'white-text body-2 opacity-on-hover'} href={`/product-learn/author/${author.slug}/`}>
                {author.name}
              </Link>
              <VerticalTextDivider />
              <span className={'body-2 white-text'}>{date}</span>
              <br />
              <Link className={'heading4 white-text'} href={`/product-learn/${articleUrl}/`}>
                {title}
              </Link>
            </div>
          </div>
        </Link>
      ) : (
        <div css={containerStyle(h, s, l, isSmall)}>
          <Link
            href={`/product-learn/${articleUrl}/`}
            className={'background-gradient'}
            css={hoverContainerStyle(h, s, l)}
          />
          {isSoon && (
            <Link className={'white-text soon opacity-on-hover'} href={`/product-learn/${articleUrl}/`}>
              🔥 SOON
            </Link>
          )}
          <div css={authorStyle}>
            <Link href={`/product-learn/author/${author.slug}/`} css={imageStyle}>
              <GatsbyImage
                image={author.authorPhoto.gatsbyImageData}
                alt={author.name}
                imgStyle={{ width: '3rem', height: '3rem' }}
              />
            </Link>
            <p className={'body-2'}>
              <Link className={'white-text'} href={`/product-learn/author/${author.slug}/`}>
                {author.name}
              </Link>
            </p>
          </div>
          <p className={'body-2 white-text'} css={dateStyle}>
            {date}
          </p>
          <Link className={'heading4 white-text'} href={`/product-learn/${articleUrl}/`}>
            {title}
          </Link>
          <div className={'body-1 white-text'}>
            <Link className={'white-text'} href={`/product-learn/${articleUrl}/`}>
              {description}
            </Link>
          </div>
        </div>
      )}
    </React.Fragment>
  )
}

export default LearnArticleCard

const containerStyle = (h: number, s: number, l: number, isSmall: boolean) => css`
  position: relative;

  &:hover {
    .background-gradient {
      opacity: 1;
    }
  }

  ${isSmall
    ? `display: grid;
    grid-template-columns: 110px auto;
    .body-2 { margin-bottom: 0.5rem; };

    &:hover {
    .background-gradient {
      background: linear-gradient(180deg, hsla(${h}, ${s}%, ${l}%, 0) 0%, hsla(${h}, ${s}%, ${l - 20}%) 30%) !important;
      }
    }
    `
    : `
    z-index: -2;
    padding: 7rem 2rem 2rem;
    border-radius: 0.5rem;
    background: linear-gradient(180deg, hsla(${h}, ${s}%, ${l}%, 0) 0%, hsla(${h}, ${s}%, ${l - 20}%) 100%);
    `};

  .soon {
    position: absolute;
    top: 1.5rem;
    left: 2rem;
    font-size: 14px;
    font-weight: 700;
  }
`

const hoverContainerStyle = (h: number, s: number, l: number) => css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, hsla(${h}, ${s}%, ${l}%, 0) 0%, hsla(${h}, ${s}%, ${l - 20}%) 30%);
  z-index: -1;
  opacity: 0;
  transition: all 200ms ease-in-out;
  border-radius: 0.5rem;
`

const iconStyle = (h: number, s: number, l: number) => css`
  position: relative;
  display: flex;
  align-self: flex-start;
  align-items: center;
  padding: 1.875rem;
  margin-right: 1rem;
  border-radius: 0.5rem;
  transition: all 200ms ease-in-out;

  background: linear-gradient(180deg, hsla(${h}, ${s}%, ${l}%, 0) 0%, hsla(${h}, ${s}%, ${l - 20}%) 100%);

  .gatsby-image-wrapper {
    margin: auto;
  }
`

const authorStyle = css`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  width: fit-content;

  &:hover {
    p {
      opacity: 0.65;
    }
  }
`

const imageStyle = css`
  background: rgba(255, 255, 255, 0.5);
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 1px solid rgba(136, 146, 158, 0.4);
  margin-right: 0.75rem;
  img {
    border-radius: 50%;
  }
`

const dateStyle = css`
  pointer-events: none;
`
